/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { humanize } from '../api';
import { isAustralia, bookingStatusLabels } from '../../variant';
import { TONES, toneFor } from './statusColors';
import { Pill, useIsReV2 } from '../../v2/parts';

// statusColors speaks the classic token names; v2Tones calls error "danger".
const V2_TONE = { error: 'danger' };

/** Australian bookings use the country's words (Offer, Holding deposit...);
 * everything else, and every UAE status, reads as before. */
function statusText(vocab, status) {
  if (vocab === 'booking' && isAustralia()) return bookingStatusLabels()[status] || humanize(status);
  return humanize(status);
}

/** Pill in the StatusBadge style, coloured by vocabulary-aware tone. */
export default function ReStatusChip({ vocab, status, label, size }) {
  const isV2 = useIsReV2();
  if (!status) return <span>—</span>;
  if (isV2) {
    const tone = toneFor(vocab, status);
    return <Pill tone={V2_TONE[tone] || tone}>{label || statusText(vocab, status)}</Pill>;
  }
  const t = TONES[toneFor(vocab, status)] || TONES.neutral;
  const small = size === 'xs';
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: small ? 1.1 : 1.5,
        py: small ? 0.3 : 0.5,
        bgcolor: t.background,
        color: t.text,
        borderRadius: 999,
        fontSize: small ? '0.6875rem' : '0.75rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
      }}
    >
      <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: t.dot, flexShrink: 0 }} />
      {label || statusText(vocab, status)}
    </Box>
  );
}

ReStatusChip.propTypes = {
  vocab: PropTypes.string.isRequired,
  status: PropTypes.string,
  label: PropTypes.string,
  size: PropTypes.oneOf(['xs', 'sm']),
};

ReStatusChip.defaultProps = { status: null, label: null, size: 'sm' };
